/**
 * Keeps the shared Ulif product card in sync with its selected color variant.
 */
class UlifProductCard extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const swatch = event.target.closest('[data-product-card-swatch]');
    if (!(swatch instanceof HTMLButtonElement) || !this.contains(swatch)) return;

    this.#selectVariant(swatch);
  };

  /** @param {HTMLButtonElement} swatch */
  #selectVariant(swatch) {
    const variantId = swatch.dataset.variantId;
    if (!variantId) return;

    this.querySelectorAll('[data-product-card-swatch]').forEach((candidate) => {
      const selected = candidate === swatch;
      candidate.classList.toggle('is-selected', selected);
      candidate.setAttribute('aria-pressed', String(selected));
    });

    const variantUrl = swatch.dataset.variantUrl;
    if (variantUrl) {
      this.querySelectorAll('[data-product-card-link]').forEach((link) => {
        if (link instanceof HTMLAnchorElement) link.href = variantUrl;
      });
    }

    this.#updateImage('[data-product-card-image]', {
      src: swatch.dataset.imageSrc,
      srcset: swatch.dataset.imageSrcset,
      width: swatch.dataset.imageWidth,
      height: swatch.dataset.imageHeight,
    });

    this.#updateImage('[data-product-card-hover-image]', {
      src: swatch.dataset.hoverSrc,
      srcset: swatch.dataset.hoverSrcset,
      width: swatch.dataset.hoverWidth,
      height: swatch.dataset.hoverHeight,
      optional: true,
    });

    this.#setText('[data-product-card-price]', swatch.dataset.price);
    this.#setOptionalText('[data-product-card-compare-price]', swatch.dataset.comparePrice);
    this.#setOptionalText('[data-product-card-discount]', swatch.dataset.discountText);
    this.#setOptionalText('[data-product-card-sale-ribbon]', swatch.dataset.saleRibbonText);

    const available = swatch.dataset.variantAvailable === 'true';
    const variantInput = this.querySelector('[data-product-card-variant-input]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
      variantInput.disabled = !available;
    }

    const addToCartContainer = this.querySelector('add-to-cart-component');
    if (addToCartContainer instanceof HTMLElement) {
      const variantMedia = swatch.dataset.variantMedia;
      if (variantMedia) addToCartContainer.dataset.productVariantMedia = variantMedia;

      const addButton = addToCartContainer.querySelector('[ref="addToCartButton"]');
      if (addButton instanceof HTMLButtonElement) addButton.disabled = !available;
    }

    this.#setText('.ulif-product-card__add-button .add-to-cart-text__content', swatch.dataset.addToCartText);
    this.#setOptionalText('[data-product-card-status]', available ? '' : swatch.dataset.addToCartText);
    this.#updateSpecs(swatch);

    this.dispatchEvent(
      new CustomEvent('ulif:product-card-variant-change', {
        bubbles: true,
        detail: { productId: this.dataset.productId, variantId, available },
      })
    );
  }

  /**
   * @param {string} selector
   * @param {{src?: string, srcset?: string, width?: string, height?: string, optional?: boolean}} state
   */
  #updateImage(selector, state) {
    const image = this.querySelector(selector);
    if (!(image instanceof HTMLImageElement)) return;

    if (state.optional && !state.src) {
      image.hidden = true;
      image.removeAttribute('src');
      image.removeAttribute('srcset');
      return;
    }

    if (!state.src) return;

    image.hidden = false;
    image.src = state.src;
    if (state.srcset) image.srcset = state.srcset;
    else image.removeAttribute('srcset');
    if (state.width) image.width = Number(state.width);
    if (state.height) image.height = Number(state.height);
  }

  /** @param {string} selector @param {string | undefined} value */
  #setText(selector, value) {
    const element = this.querySelector(selector);
    if (element && value !== undefined) element.textContent = value;
  }

  /** @param {string} selector @param {string | undefined} value */
  #setOptionalText(selector, value) {
    const element = this.querySelector(selector);
    if (!(element instanceof HTMLElement)) return;

    const text = value?.trim() || '';
    element.textContent = text;
    element.hidden = text.length === 0;
  }

  /** @param {HTMLButtonElement} swatch */
  #updateSpecs(swatch) {
    const specs = this.querySelector('[data-product-card-specs]');
    if (!(specs instanceof HTMLElement)) return;

    const load = swatch.dataset.specLoad?.trim() || '';
    const width = swatch.dataset.specWidth?.trim() || '';
    const widthMax = swatch.dataset.specWidthMax?.trim() || '';

    this.#setOptionalText('[data-product-card-spec-load]', load ? `Load: ${load} lbs` : '');
    this.#setOptionalText(
      '[data-product-card-spec-width]',
      width ? `Width: ${width}${widthMax ? `-${widthMax}` : ''} in` : ''
    );

    specs.hidden = !load && !width;
  }
}

if (!customElements.get('ulif-product-card')) {
  customElements.define('ulif-product-card', UlifProductCard);
}
